import { useChartColors } from '../lib/accessibility'
import { apiError } from '../lib/errors'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { Thermometer, Droplets, Cloud, Plus, X, Waves, Sun, Snowflake } from 'lucide-react'
import { weatherApi, frostApi, type WeatherStation, type FrostForecast, type FrostNight } from '../lib/api'

// ── Design tokens ───────────────────────────────────────────────────────────
const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     'var(--color-bg-card-2)',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#4CAF50',
  red:       '#F87171',
  blue:      '#60A5FA',
  teal:      '#2DD4BF',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
}

const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card2,
  border: `1px solid ${C.border}`, borderRadius: 8,
  padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
}
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted,
  marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.06em',
}

const tooltipStyle = {
  background: C.card2, border: `1px solid ${C.border}`,
  borderRadius: 8, fontSize: 12, color: C.text,
}

// ── Add Station Modal ────────────────────────────────────────────────────────
function AddStationModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ provider: 'wunderground', station_id: '', name: '' })
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: string) => setForm(f => ({ ...f, [k]: v }))

  const save = useMutation({
    mutationFn: () => weatherApi.addStation({ provider: form.provider, station_id: form.station_id, name: form.name, is_primary: false }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['weather-stations'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to add station')),
  })

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 400, maxWidth: '95vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Add weather station</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={lbl}>Provider</label>
            <select style={{ ...inp, cursor: 'pointer' }} value={form.provider} onChange={e => set('provider', e.target.value)}>
              <option value="wunderground">Weather Underground (PWS)</option>
              <option value="noaa">NOAA</option>
              <option value="davis">Davis Instruments</option>
              <option value="ecowitt">Ecowitt</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Station ID</label>
            <input style={inp} value={form.station_id} onChange={e => set('station_id', e.target.value)} placeholder="KFLMIAMI123" />
            <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>Find your PWS ID at wunderground.com/dashboard/pws</div>
          </div>
          <div>
            <label style={lbl}>Display name</label>
            <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Farm Main Station" />
          </div>
          {error && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending || !form.station_id} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.blue, border: 'none', color: C.bg, cursor: save.isPending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {save.isPending ? 'Adding…' : 'Add station'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Current conditions card ──────────────────────────────────────────────────
function WeatherStat({ icon, label, value, unit, accent }: {
  icon: React.ReactNode; label: string; value: string | number | undefined; unit?: string; accent?: string
}) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: accent ?? C.textMuted }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, color: accent ?? C.text, lineHeight: 1 }}>
        {value !== undefined ? value : <span style={{ fontSize: 20, color: C.textFaint }}>—</span>}
        {value !== undefined && unit && <span style={{ fontSize: 15, fontWeight: 400, color: C.textMuted, marginLeft: 3 }}>{unit}</span>}
      </div>
    </div>
  )
}

// ── Frost risk card ──────────────────────────────────────────────────────────
const FROST_META: Record<string, { label: string; color: string }> = {
  freeze: { label: 'Freeze', color: '#60A5FA' },
  frost:  { label: 'Frost',  color: '#93C5FD' },
  watch:  { label: 'Frost watch', color: '#F5A623' },
  none:   { label: '', color: 'var(--color-text-faint)' },
}
function FrostCard() {
  const { data } = useQuery({ queryKey: ['frost-forecast'], queryFn: () => frostApi.forecast().then(r => r.data), refetchInterval: 60 * 60 * 1000 })
  const fc: FrostForecast | undefined = data
  if (!fc) return null

  const alert = fc.next_freeze ?? fc.next_frost
  const alertColor = fc.next_freeze ? FROST_META.freeze.color : FROST_META.frost.color
  // Show the upcoming nights that carry any risk; if none, show the next few lows anyway.
  const risky = (fc.nights ?? []).filter(n => n.level !== 'none')
  const shown: FrostNight[] = (risky.length ? risky : (fc.nights ?? [])).slice(0, 6)

  return (
    <div style={{ background: C.card, border: `1px solid ${alert ? alertColor + '66' : C.border}`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: alert ? alertColor : C.textMuted }}>
        <Snowflake size={15} />
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Frost risk</span>
        {fc.location && <span style={{ fontSize: 11, color: C.textFaint, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· {fc.location}</span>}
      </div>

      {!fc.found ? (
        <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>{fc.message || 'Frost forecast unavailable.'}</p>
      ) : alert ? (
        <div style={{ fontSize: 14, color: C.text, marginBottom: shown.length ? 14 : 0 }}>
          <strong style={{ color: alertColor }}>{fc.next_freeze ? 'Freeze' : 'Frost'} expected {alert.label.toLowerCase()}</strong>
          {' '}— low <strong>{alert.low_f}°F</strong>. Protect tender plants / consider a frost-protection irrigation run.
        </div>
      ) : (
        <p style={{ fontSize: 13, color: C.textMuted, margin: shown.length ? '0 0 14px' : 0 }}>No frost expected in the 7-day forecast.</p>
      )}

      {fc.found && shown.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {shown.map(n => {
            const m = FROST_META[n.level] ?? FROST_META.none
            return (
              <div key={n.date + n.label} title={n.short_forecast} style={{ flex: '1 1 92px', minWidth: 92, padding: '8px 10px', borderRadius: 8, background: C.card2, border: `1px solid ${n.level !== 'none' ? m.color + '55' : C.border}` }}>
                <div style={{ fontSize: 11, color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: n.level !== 'none' ? m.color : C.text }}>{n.low_f}°F</div>
                {n.level !== 'none' && <div style={{ fontSize: 10, fontWeight: 600, color: m.color }}>{m.label}</div>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────
export function WeatherPage() {
  const chartC = useChartColors()
  const [showAddStation, setShowAddStation] = useState(false)
  const [historyDays, setHistoryDays]       = useState(14)
  const [activeChart, setActiveChart]       = useState<'temp' | 'rain' | 'humidity'>('temp')

  const { data: currentData } = useQuery({
    queryKey: ['weather-current'],
    queryFn: () => weatherApi.current(),
    refetchInterval: 5 * 60 * 1000,
  })
  const { data: historyData, isLoading: histLoading } = useQuery({
    queryKey: ['weather-history', historyDays],
    queryFn: () => weatherApi.history(historyDays),
  })
  const { data: stationsData } = useQuery({
    queryKey: ['weather-stations'],
    queryFn: () => weatherApi.stations(),
  })
  const { data: etData } = useQuery({
    queryKey: ['weather-et'],
    queryFn: () => weatherApi.et(),
  })

  const current   = currentData?.data
  const history   = historyData?.data ?? []
  const stations: WeatherStation[] = stationsData?.data ?? []
  const et        = etData?.data?.et_mm

  const chartData = history.map(h => ({
    date:     new Date(h.observed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    temp_c:   h.temp_c,
    humidity: h.humidity_pct,
    rain_mm:  h.precip_mm,
  }))

  const tabBtn = (id: typeof activeChart, label: string) => (
    <button onClick={() => setActiveChart(id)} style={{
      padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
      background: activeChart === id ? C.blue + '22' : 'transparent',
      color: activeChart === id ? C.blue : C.textMuted,
      border: activeChart === id ? `1px solid ${C.blue}44` : `1px solid transparent`,
      cursor: 'pointer',
    }}>{label}</button>
  )

  return (
    <main id="main-content" style={{ padding: 32, fontFamily: '"DM Sans", system-ui, sans-serif', background: C.bg, minHeight: '100dvh', color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, fontFamily: '"Syne", system-ui', margin: '0 0 4px' }}>Weather</h1>
          <p style={{ color: C.textMuted, fontSize: 13, margin: 0 }}>
            {stations.length > 0
              ? `${stations.length} station${stations.length > 1 ? 's' : ''} · ${stations.find(s => s.is_primary)?.name ?? stations[0]?.name}`
              : 'Connect a weather station to see live data'}
          </p>
        </div>
        <button onClick={() => setShowAddStation(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, background: C.blue, border: 'none', color: C.bg, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={15} /> Add Station
        </button>
      </div>

      {/* Current conditions row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 24 }}>
        <WeatherStat icon={<Thermometer size={14} />} label="Temperature" value={current?.temp_c !== undefined ? current.temp_c.toFixed(1) : undefined} unit="°C" accent={C.amber} />
        <WeatherStat icon={<Droplets size={14} />}    label="Humidity"    value={current?.humidity_pct !== undefined ? `${current.humidity_pct.toFixed(0)}` : undefined} unit="%" accent={C.blue} />
        <WeatherStat icon={<Cloud size={14} />}       label="Rainfall"    value={current?.precip_mm !== undefined ? current.precip_mm.toFixed(1) : undefined} unit="mm" accent={C.teal} />
        <WeatherStat icon={<Waves size={14} />}       label="ET today"    value={et !== undefined ? et.toFixed(1) : undefined} unit="mm" accent={C.green} />
      </div>

      {/* Frost risk (§186) */}
      <FrostCard />

      {/* History chart */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {tabBtn('temp', 'Temperature')}
            {tabBtn('rain', 'Rainfall')}
            {tabBtn('humidity', 'Humidity')}
          </div>
          <div style={{ display: 'flex', gap: 5 }}>
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setHistoryDays(d)} style={{
                padding: '5px 11px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                background: historyDays === d ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : C.card2,
                color: historyDays === d ? C.amber : C.textMuted,
                border: `1px solid ${historyDays === d ? 'color-mix(in srgb, var(--color-primary) 33%, transparent)' : C.border}`,
                cursor: 'pointer',
              }}>{d}d</button>
            ))}
          </div>
        </div>

        {histLoading ? (
          <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>Loading…</div>
        ) : chartData.length === 0 ? (
          <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, color: C.textMuted }}>
            <Sun size={32} style={{ opacity: 0.3 }} />
            <span>No weather history yet</span>
            <span style={{ fontSize: 12, color: C.textFaint }}>Add a station and wait for observations to come in</span>
          </div>
        ) : activeChart === 'temp' ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartC.grid} />
              <XAxis dataKey="date" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} unit="°C" />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v?.toFixed(1)}°C`, 'Temperature']} />
              <Line type="monotone" dataKey="temp_c" stroke={chartC.accent} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : activeChart === 'rain' ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartC.grid} vertical={false} />
              <XAxis dataKey="date" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} unit=" mm" />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v?.toFixed(1)} mm`, 'Rainfall']} />
              <Bar dataKey="rain_mm" fill={C.teal} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartC.grid} />
              <XAxis dataKey="date" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} unit="%" domain={[0, 100]} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v?.toFixed(0)}%`, 'Humidity']} />
              <Line type="monotone" dataKey="humidity" stroke={C.blue} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Dual-axis mini overview */}
      {chartData.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>
            Temperature + Rainfall overview
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData} margin={{ top: 4, right: 32, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartC.grid} />
              <XAxis dataKey="date" tick={{ fill: chartC.faint, fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis yAxisId="temp" tick={{ fill: chartC.faint, fontSize: 10 }} tickLine={false} axisLine={false} unit="°C" />
              <YAxis yAxisId="rain" orientation="right" tick={{ fill: chartC.faint, fontSize: 10 }} tickLine={false} axisLine={false} unit="mm" />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, color: C.textMuted }} />
              <Line yAxisId="temp" type="monotone" dataKey="temp_c"  stroke={chartC.accent} strokeWidth={2} dot={false} name="Temp (°C)" />
              <Line yAxisId="rain" type="monotone" dataKey="rain_mm" stroke={C.teal}  strokeWidth={1.5} dot={false} strokeDasharray="4 2" name="Rain (mm)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Stations */}
      {stations.length > 0 ? (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>Connected Stations</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stations.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: C.card2, borderRadius: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.green, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{s.name || s.station_id}</div>
                  <div style={{ fontSize: 11, color: C.textFaint, marginTop: 1 }}>
                    {s.provider} · {s.station_id}
                    {s.is_primary && <span style={{ marginLeft: 8, color: C.amber }}>Primary</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setShowAddStation(true)}
            style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 12 }}>
            <Plus size={13} /> Add another station
          </button>
        </div>
      ) : (
        <div style={{ background: C.card, border: `2px dashed ${C.border}`, borderRadius: 12, padding: '40px 24px', textAlign: 'center' }}>
          <Cloud size={32} style={{ color: C.textFaint, marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontSize: 14, color: C.textMuted, margin: '0 0 6px' }}>No weather stations connected</p>
          <p style={{ fontSize: 12, color: C.textFaint, margin: '0 0 20px' }}>Connect a Weather Underground PWS to see live conditions and build up historical data.</p>
          <button onClick={() => setShowAddStation(true)}
            style={{ padding: '9px 20px', borderRadius: 9, background: C.blue, border: 'none', color: C.bg, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Connect first station
          </button>
        </div>
      )}

      {showAddStation && <AddStationModal onClose={() => setShowAddStation(false)} />}
    </main>
  )
}
